import { useEffect, useRef, useState } from 'react'

/**
 * Live-URL preview — replaces the old static screenshot. Renders the
 * template's real deployed site in a sandboxed <iframe>, mounted only when
 * scrolled near the viewport (IntersectionObserver) so a 40-card gallery
 * doesn't load 40 live sites at once.
 *
 * Thumbnail mode (gallery cards, `interactive=false`): the iframe is rendered
 * at a fixed desktop width (DESIGN_W) and scaled down to fit the card, so it
 * reads like a desktop screenshot rather than a cramped mobile layout. It's
 * pointer-events:none — the parent <a> owns the click.
 *
 * Interactive mode (detail hero): the iframe fills its frame 1:1 and scrolls.
 */
const DESIGN_W = 1280
const ASPECT = 10 / 16 // height / width — matches the 16:10 frame

export default function LivePreview({
  url,
  title,
  eager = false,
  interactive = false,
}: {
  url: string
  title: string
  eager?: boolean
  interactive?: boolean
}) {
  const hostRef = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(eager)
  const [failed, setFailed] = useState(false)
  const [scale, setScale] = useState(0.3)

  // Mount the iframe only when near the viewport.
  useEffect(() => {
    if (visible) return
    const node = hostRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '300px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [visible])

  // Thumbnail scaling: keep the desktop-width iframe fitted to the host width.
  useEffect(() => {
    if (interactive) return
    const node = hostRef.current
    if (!node) return
    const measure = () => setScale(node.clientWidth / DESIGN_W)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [interactive, visible])

  const thumbStyle = !interactive
    ? ({
        width: `${DESIGN_W}px`,
        height: `${DESIGN_W * ASPECT}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      } as const)
    : undefined

  return (
    <span
      ref={hostRef}
      className={`od-tpl-live${interactive ? ' is-interactive' : ' is-thumb'}`}
    >
      {visible && !failed ? (
        <iframe
          src={url}
          title={title}
          loading={eager ? 'eager' : 'lazy'}
          // Allow scripts so the live site renders, but keep it sandboxed.
          sandbox="allow-scripts allow-same-origin allow-popups"
          scrolling={interactive ? 'auto' : 'no'}
          tabIndex={interactive ? 0 : -1}
          style={thumbStyle}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="od-tpl-img-fallback" aria-hidden={!failed}>
          {title}
        </span>
      )}
    </span>
  )
}
